import React, { useEffect, useState, useCallback, useRef } from "react";
import FloorPlanner from "./components/FloorPlanner";
import Sidebar from "./components/Sidebar";
import { v4 as uuidv4 } from "uuid";
import { auth } from '../../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUserEquipment, updateUserEquipment, createMasterEquipment, updateMasterEquipment, uploadUserEquipmentImage, uploadMasterEquipmentImage } from '../../services/api';
import { exportToDXF, downloadDXF } from '../../utils/dxfExporter';
import { useAuth } from '../../contexts/AuthContext';

const INITIAL_FLOOR = '1F';

// デフォルトのフロア設定を生成する関数
const generateDefaultFloors = (count) => {
  const floors = {};
  const floorNames = {};
  for (let i = 1; i <= count; i++) {
    const floorKey = `${i}F`;
    floors[floorKey] = [];
    floorNames[floorKey] = floorKey;
  }
  return { floors, floorNames };
};

// データURLをBlobに変換するヘルパー関数
const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export default function HouserPlanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // 既存データとの互換性を保つためのヘルパー関数
  const normalizeFloorData = (rooms) => {
    if (!rooms) return {'1F': [], '2F': []};
    
    // 既に正しい形式（オブジェクト）の場合
    if (typeof rooms === 'object' && !Array.isArray(rooms)) {
      return rooms;
    }
    
    // 旧形式（配列）の場合、1Fとして扱う
    if (Array.isArray(rooms)) {
      return {'1F': rooms, '2F': []};
    }
    
    // その他の場合はデフォルト
    return {'1F': [], '2F': []};
  };

  // フロア管理の状態
  const [floorCount, setFloorCount] = useState(() => {
    const normalizedFloors = normalizeFloorData(location.state?.floorRooms);
    return Object.keys(normalizedFloors).length;
  });
  
  const [floorNames, setFloorNames] = useState(() => {
    const normalizedFloors = normalizeFloorData(location.state?.floorRooms);
    const existingFloorNames = location.state?.floorNames || {};
    const names = {};
    Object.keys(normalizedFloors).forEach(key => {
      names[key] = existingFloorNames[key] || key;
    });
    return names;
  });

  const [floorRooms, setFloorRooms] = useState(() => {
    return normalizeFloorData(location.state?.floorRooms);
  });

  const [currentFloor, setCurrentFloor] = useState(() => {
    const normalizedFloors = normalizeFloorData(location.state?.floorRooms);
    const availableFloors = Object.keys(normalizedFloors);
    return availableFloors.includes(INITIAL_FLOOR) ? INITIAL_FLOOR : availableFloors[0];
  });
  
  const [selectedId, setSelectedId] = useState(null);
  const [targetUserId, setTargetUserId] = useState(location.state?.targetUserId || auth.currentUser?.uid || null);
  const [mode, setMode] = useState(location.state?.mode || 'create');
  const [user_equipment_id, setUserEquipmentId] = useState(location.state?.user_equipment_id || null);
  const [item_id, setItemId] = useState(location.state?.item_id || null);
  const [existingImages, setExistingImages] = useState(location.state?.images || []);
  const [masterEquipmentData, setMasterEquipmentData] = useState(location.state?.masterEquipmentData || null);

  // UI状態
  const [showFloorSettings, setShowFloorSettings] = useState(false);
  const [editingFloorName, setEditingFloorName] = useState(null);
  const [originalFloorName, setOriginalFloorName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // FloorPlannerの参照を取得するためのref
  const floorPlannerRef = useRef(null);

  // フロア数を変更したときの処理
  const handleFloorCountChange = (newCount) => {
    if (newCount < 1) return;
    
    setFloorCount(newCount);
    
    // 新しいフロア構成を作成
    const { floors: newFloors, floorNames: newFloorNames } = generateDefaultFloors(newCount);
    
    // 既存のフロアデータを保持
    Object.keys(floorRooms).forEach(floorKey => {
      if (newFloors[floorKey] !== undefined) {
        newFloors[floorKey] = floorRooms[floorKey];
        newFloorNames[floorKey] = floorNames[floorKey] || floorKey;
      }
    });
    
    setFloorRooms(newFloors);
    setFloorNames(newFloorNames);
    
    // 現在のフロアが削除された場合、最初のフロアに切り替え
    if (!newFloors[currentFloor]) {
      setCurrentFloor(Object.keys(newFloors)[0]);
    }
  };

  // フロア名を変更する処理
  const handleFloorNameChange = (floorKey, newName) => {
    setFloorNames(prev => ({
      ...prev,
      [floorKey]: newName
    }));
  };

  // フロア名編集を開始
  const startEditingFloorName = (floorKey) => {
    setOriginalFloorName(floorNames[floorKey]);
    setEditingFloorName(floorKey);
  };

  // フロア名編集を完了
  const finishEditingFloorName = () => {
    setEditingFloorName(null);
    setOriginalFloorName('');
  };

  // フロア名編集をキャンセル
  const cancelEditingFloorName = (floorKey) => {
    setFloorNames(prev => ({
      ...prev,
      [floorKey]: originalFloorName
    }));
    setEditingFloorName(null);
    setOriginalFloorName('');
  };

  // 現在のフロアの部屋リストとそのセッター
  const roomsForCurrentFloor = floorRooms[currentFloor] || [];
  const setRoomsForCurrentFloor = (updater) => {
    setFloorRooms(prevFloorRooms => {
      const currentRoomsList = prevFloorRooms[currentFloor] || [];
      const newRoomsList = typeof updater === 'function' ? updater(currentRoomsList) : updater;
      return {
        ...prevFloorRooms,
        [currentFloor]: newRoomsList,
      };
    });
  };

  const handleAddRoom = (roomType) => {
    const newRoom = {
      id: uuidv4(),
      x: roomType.x || 100,
      y: roomType.y || 100,
      x2: roomType.x2 || 200,
      y2: roomType.y2 || 100,
      width: roomType.width || 100, // サイドバーで定義された幅を使用、なければデフォルト
      height: roomType.height || 100, // サイドバーで定義された高さを使用、なければデフォルト
      name: roomType.name,
      fill: roomType.fill || "#add8e6",
      image: roomType.image || null, // ★ 画像パスも追加 (roomオブジェクトにimageプロパティがある場合)
      rotation: 0,
      type: roomType.type || 'room',
      isFreeSize: roomType.isFreeSize || false,
      strokeWidth: roomType.strokeWidth || 8,
    };
    setRoomsForCurrentFloor((prev) => [...prev, newRoom]);
    setSelectedId(newRoom.id);
  };

  const handleRotateRoom = (roomId, angleIncrement) => {
    setRoomsForCurrentFloor((prevRoomsList) =>
      prevRoomsList.map((room) => {
        if (room.id === roomId) {
          const currentRotation = room.rotation || 0;
          let newRotation = (currentRotation + angleIncrement) % 360;
          if (newRotation < 0) {
            newRotation += 360;
          }
          return { ...room, rotation: newRotation };
        }
        return room;
      })
    );
  };

  // 削除処理 (ボタンとキーボードで共通化)
  const handleDelete = useCallback(() => {
    if (selectedId) {
      setFloorRooms(prevFloorRooms => {
        const currentRoomsList = prevFloorRooms[currentFloor] || [];
        const newRoomsList = currentRoomsList.filter((room) => room.id !== selectedId);
        return {
          ...prevFloorRooms,
          [currentFloor]: newRoomsList,
        };
      });
      setSelectedId(null);
    }
  }, [selectedId, currentFloor, setFloorRooms]);

  const handleChangeFloor = (floor) => {
    setCurrentFloor(floor);
    setSelectedId(null); // フロア切り替え時に選択を解除
  };

  const handleSaveAndClose = async () => {
    if (isSaving) return;
    setIsSaving(true);
    
    try {
      let createdEquipmentId = null;
      let createdEquipmentData = null;

      // 1. まず間取りデータを保存（フロア名も含める）
      const saveData = {
        rooms: floorRooms,
        floorNames: floorNames,
        floorCount: floorCount
      };

      if (mode === 'create') {
        const result = await createUserEquipment('floorplan', {
          item_type: 'floorplan',
          user_id: targetUserId,
          ...saveData,
        });
        createdEquipmentId = result.id;
      } else if (mode === 'edit') {
        await updateUserEquipment('floorplan', user_equipment_id, {
          user_id: targetUserId,
          ...saveData,
        });
        createdEquipmentId = user_equipment_id;
      } else if(mode === 'new_master'){
        createdEquipmentData = await createMasterEquipment('floorplan', saveData);
        createdEquipmentId = createdEquipmentData.id;
      } else if (mode === 'edit_master') {
        createdEquipmentData = await updateMasterEquipment('floorplan', item_id, saveData);
        createdEquipmentId = item_id;
      }

      // 2. 全フロアの画像をキャプチャして保存
      if (floorPlannerRef.current && createdEquipmentId) {
        try {
          const floorKeys = Object.keys(floorRooms);
          const imageUrls = [];
          const originalFloor = currentFloor;

          // 各フロアの画像を個別にキャプチャ
          for (const floorKey of floorKeys) {
            // フロアを切り替え
            setCurrentFloor(floorKey);
            
            // レンダリング完了を待つ
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const dataURL = floorPlannerRef.current.captureImage();
            if (dataURL) {
              const imageBlob = dataURLtoBlob(dataURL);
              const floorName = floorNames[floorKey] || floorKey;
              const imageFile = new File([imageBlob], `__FP_PLAN__${floorName}_${Date.now()}.png`, {
                type: 'image/png'
              });

              // 画像をアップロード
              if (mode === 'create' || mode === 'edit') {
                const uploadResult = await uploadUserEquipmentImage('floorplan', imageFile, auth.currentUser, targetUserId, `${floorName}平面図`);
                imageUrls.push(uploadResult);
              } else if (mode === 'new_master' || mode === 'edit_master') {
                const uploadResult = await uploadMasterEquipmentImage(imageFile, `${floorName}平面図`);
                imageUrls.push(uploadResult);
              }
              
              console.log(`${floorName}の画像保存完了`);
            }
          }

          // 合成画像を生成
          if (imageUrls.length > 0) {
            const compositeImageUrl = await createCompositeImage(floorKeys, floorNames);
            if (compositeImageUrl) {
              const compositeImageBlob = dataURLtoBlob(compositeImageUrl);
              const compositeImageFile = new File([compositeImageBlob], `__FP_COMPOSITE__${Date.now()}.png`, {
                type: 'image/png'
              });

              // 合成画像をアップロード
              if (mode === 'create' || mode === 'edit') {
                const uploadResult = await uploadUserEquipmentImage('floorplan', compositeImageFile, auth.currentUser, targetUserId, '全フロア平面図');
                imageUrls.push(uploadResult);
              } else if (mode === 'new_master' || mode === 'edit_master') {
                const uploadResult = await uploadMasterEquipmentImage(compositeImageFile, '全フロア平面図');
                imageUrls.push(uploadResult);
              }
              
              console.log('合成画像の保存完了');
            }
          }

          // 画像配列を更新（間取り関連の画像のみ上書き、他の画像は保持）
          if (imageUrls.length > 0) {
            let currentImages = [];
            
            // 既存の画像を取得
            if (mode === 'edit') {
              currentImages = existingImages || [];
            } else if (mode === 'edit_master') {
              // マスタ設備の場合、優先順位：masterEquipmentData > existingImages > 空配列
              currentImages = (masterEquipmentData && masterEquipmentData.images) ? 
                masterEquipmentData.images : 
                (existingImages || []);
            }
            
            // 間取り関連の画像を除外
            // ユーザー登録設備とマスタ設備で異なる識別方法を適用
            const nonFloorplanImages = currentImages.filter(imageItem => {
              if (!imageItem) {
                return false;
              }
              
              // 新旧形式対応：文字列かオブジェクトかを判定
              const imageUrl = typeof imageItem === 'string' ? imageItem : imageItem.url;
              if (!imageUrl || typeof imageUrl !== 'string') {
                return false;
              }
              
              // URLからファイル名を取得
              const fileNameLower = imageUrl.toLowerCase();
              
              let isFloorplan = false;
              
              if (mode === 'edit' || mode === 'create') {
                // ユーザー登録設備の場合：特殊記号による間取り画像識別
                isFloorplan = fileNameLower.includes('__fp_plan__') || 
                            fileNameLower.includes('__fp_composite__')
              } else if (mode === 'edit_master' || mode === 'new_master') {
                // マスタ設備の場合：特殊記号による間取り画像識別
                isFloorplan = fileNameLower.includes('__fp_plan__') || 
                            fileNameLower.includes('__fp_composite__')
              }              
              return !isFloorplan;
            });
            
            
            // 新しい画像配列を構築：合成画像を最初に、その後各フロア画像、最後に間取り以外の既存画像
            const reorderedImageUrls = [];
            
            // 合成画像を最初に配置（最後の画像が合成画像）
            if (imageUrls.length > 0) {
              const compositeImage = imageUrls[imageUrls.length - 1]; // 最後が合成画像
              reorderedImageUrls.push(compositeImage);
              
              // 各フロアの画像を追加（合成画像以外）
              for (let i = 0; i < imageUrls.length - 1; i++) {
                reorderedImageUrls.push(imageUrls[i]);
              }
            }
            
            // 最終的な画像配列：[合成画像, フロア画像1, フロア画像2, ..., 間取り以外の既存画像]
            const updatedImages = [...reorderedImageUrls, ...nonFloorplanImages];
                        
            if (mode === 'create' || mode === 'edit') {
              await updateUserEquipment('floorplan', createdEquipmentId, {
                user_id: targetUserId,
                ...saveData,
                images: updatedImages
              });
              // 既存画像のstateを更新
              setExistingImages(updatedImages);
            } else if (mode === 'new_master' || mode === 'edit_master') {
              const result = await updateMasterEquipment('floorplan', createdEquipmentId, {
                ...saveData,
                images: updatedImages
              });
              // マスタ設備データを更新
              setMasterEquipmentData(result);
              // 既存画像のstateを更新
              setExistingImages(updatedImages);
            }
          }

          // 元のフロアに戻す
          setCurrentFloor(originalFloor);
          
          console.log(`全${floorKeys.length}フロア + 合成画像の保存が完了しました`);
        } catch (imageError) {
          console.error('間取り画像の保存に失敗しました:', imageError);
          // 画像保存に失敗しても間取りデータ自体は保存されているので、処理を継続
        }
      }

      navigate(-1);
    } catch (error) {
      console.error('保存に失敗しました:', error);
      alert('保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSaving(false);
    }
  };

  // DXFエクスポート機能
  const handleExportDXF = () => {
    try {
      const dxfContent = exportToDXF(floorRooms, floorNames);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `floorplan_${timestamp}.dxf`;
      downloadDXF(dxfContent, filename);
      
      // 成功通知
      const notification = document.createElement('div');
      notification.textContent = 'DXFファイルをエクスポートしました';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 24px;
        border-radius: 4px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
      console.log('DXFエクスポートが完了しました');
    } catch (error) {
      console.error('DXFエクスポートに失敗しました:', error);
      alert('DXFエクスポートに失敗しました。もう一度お試しください。');
    }
  };

  // 一時保存機能（画像生成なし、画面も閉じない）
  const handleTempSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    
    try {
      // 間取りデータのみ保存（フロア名も含める）
      const saveData = {
        rooms: floorRooms,
        floorNames: floorNames,
        floorCount: floorCount
      };

      if (mode === 'create') {
        const result = await createUserEquipment('floorplan', {
          item_type: 'floorplan',
          user_id: targetUserId,
          ...saveData,
        });
        // 新規作成の場合、次回は編集モードに切り替え
        setMode('edit');
        setUserEquipmentId(result.id);
      } else if (mode === 'edit') {
        await updateUserEquipment('floorplan', user_equipment_id, {
          user_id: targetUserId,
          ...saveData,
        });
      } else if(mode === 'new_master'){
        const result = await createMasterEquipment('floorplan', saveData);
        // 新規作成の場合、次回は編集モードに切り替え
        setMode('edit_master');
        setItemId(result.id);
        // マスタ設備データを更新
        setMasterEquipmentData(result);
      } else if (mode === 'edit_master') {
        const result = await updateMasterEquipment('floorplan', item_id, saveData);
        // マスタ設備データを更新
        setMasterEquipmentData(result);
      }

      // 成功通知（簡易版）
      const notification = document.createElement('div');
      notification.textContent = '一時保存しました';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 24px;
        border-radius: 4px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      console.log('一時保存が完了しました');
    } catch (error) {
      console.error('一時保存に失敗しました:', error);
      alert('一時保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSaving(false);
    }
  };

  // 合成画像を生成する関数
  const createCompositeImage = async (floorKeys, floorNames) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // キャンバスサイズを設定（各フロアを縦に並べる）
    const floorWidth = 1000; // FloorPlannerの固定幅
    const floorHeight = 800; // FloorPlannerの固定高さ
    const margin = 50; // フロア間の余白
    const titleHeight = 40; // フロア名表示エリアの高さ
    
    canvas.width = floorWidth;
    canvas.height = (floorHeight + titleHeight + margin) * floorKeys.length + margin;
    
    // 背景を白に設定
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    if (floorKeys.length === 0) {
      return null;
    }

    // 元のフロアを保存
    const originalFloor = currentFloor;

    try {
      // 各フロアを順次処理
      for (let index = 0; index < floorKeys.length; index++) {
        const floorKey = floorKeys[index];
        
        // フロアを切り替え
        setCurrentFloor(floorKey);
        
        // レンダリング完了を待つ（より長い時間で確実に）
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // 画像をキャプチャ
        const dataURL = floorPlannerRef.current?.captureImage();
        
        if (dataURL) {
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              try {
                const yPosition = (floorHeight + titleHeight + margin) * index + margin;
                
                // フロア名を描画
                ctx.fillStyle = '#000000';
                ctx.font = '24px Arial';
                ctx.textAlign = 'center';
                const floorName = floorNames[floorKey] || floorKey;
                ctx.fillText(floorName, floorWidth / 2, yPosition + 30);
                
                // フロア画像を描画
                ctx.drawImage(img, 0, yPosition + titleHeight, floorWidth, floorHeight);
                
                console.log(`${floorName}の合成画像描画完了`);
                resolve();
              } catch (error) {
                reject(error);
              }
            };
            img.onerror = reject;
            img.src = dataURL;
          });
        } else {
          console.warn(`${floorKey}の画像キャプチャに失敗しました`);
        }
      }

      // 元のフロアに戻す
      setCurrentFloor(originalFloor);
      
      console.log('全フロアの合成画像生成完了');
      return canvas.toDataURL('image/png');
      
    } catch (error) {
      console.error('合成画像生成中にエラーが発生しました:', error);
      // エラーが発生しても元のフロアに戻す
      setCurrentFloor(originalFloor);
      return null;
    }
  };

  const updateSelectedRoom = (room) => {
    setRoomsForCurrentFloor((prevRoomsList) =>
      prevRoomsList.map((r) => (r.id === room.id ? room : r))
    );
  };

  // targetUserId が未設定の場合、ログインユーザーのUIDをセットする
  useEffect(() => {
    if (!targetUserId && auth.currentUser) {
      setTargetUserId(auth.currentUser.uid);
    }
    // location.state.targetUserId が変更された場合にも追従
    if (location.state?.targetUserId && location.state.targetUserId !== targetUserId) {
      setTargetUserId(location.state.targetUserId);
    }
  }, [targetUserId, location.state?.targetUserId]);

  // コンポーネント初期化時にlocation.stateから既存データを確実に設定
  useEffect(() => {
    // location.stateの詳細な検証
    if (location.state) {
      // 既存画像の設定
      if (location.state.images && Array.isArray(location.state.images)) {
        setExistingImages(location.state.images);
      }
      
      // マスタ設備データの設定
      if (location.state.masterEquipmentData) {
        setMasterEquipmentData(location.state.masterEquipmentData);
      }
    }
  }, []);

  // --- ここから追加 ---
  // キーボードイベントの処理
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        // 編集可能な要素にフォーカスがある場合は削除処理を実行しない
        if (event.target && (
          event.target.tagName === "INPUT" || 
          event.target.tagName === "TEXTAREA" || 
          event.target.tagName === "SELECT" ||
          event.target.isContentEditable
        )) {
          return;
        }
        if (selectedId) {
          handleDelete();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedId, handleDelete]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar
          onAddRoom={handleAddRoom}
          rooms={roomsForCurrentFloor} // Sidebarにroomsを渡す (現在はカレントフロアの部屋)
          selectedId={selectedId} // SidebarにselectedIdを渡す
          onRotateRoom={handleRotateRoom} // 回転ハンドラを渡す
          updateSelectedRoom={updateSelectedRoom} // 選択された部屋を更新するハンドラを渡す
        />
        <FloorPlanner
          rooms={roomsForCurrentFloor} // 現在のフロアの部屋を渡す
          setRooms={setRoomsForCurrentFloor} // 現在のフロアの部屋を更新する関数を渡す
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          ref={floorPlannerRef} // 画像キャプチャ用のref
        />
      </div>

      {/* 下部：フロア管理と保存ボタン */}
      <div
        style={{
          padding: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #ccc",
          backgroundColor: "#f8f8f8",
        }}
      >
        {/* フロア設定ボタン */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => setShowFloorSettings(!showFloorSettings)}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              cursor: "pointer",
              border: "1px solid #ccc",
              backgroundColor: showFloorSettings ? "#e0e0e0" : "white",
              borderRadius: 4
            }}
          >
            階設定
          </button>
          
          {showFloorSettings && (
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 8,
              padding: "8px 12px",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: 4,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}>
              <label style={{ fontSize: "12px" }}>階数:</label>
              <input
                type="number"
                min="1"
                max="10"
                value={floorCount}
                onChange={(e) => handleFloorCountChange(parseInt(e.target.value) || 1)}
                style={{ 
                  width: 50, 
                  padding: 4, 
                  fontSize: "12px",
                  border: "1px solid #ccc",
                  borderRadius: 2
                }}
              />
            </div>
          )}
        </div>

        {/* フロア切り替えボタン */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {Object.keys(floorRooms).map(floorKey => (
            <div key={floorKey} style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {editingFloorName === floorKey ? (
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <input
                    type="text"
                    value={floorNames[floorKey]}
                    onChange={(e) => handleFloorNameChange(floorKey, e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        finishEditingFloorName();
                      }
                      if (e.key === 'Escape') {
                        cancelEditingFloorName(floorKey);
                      }
                    }}
                    style={{
                      width: 60,
                      padding: "4px 8px",
                      fontSize: "12px",
                      border: "1px solid #3182ce",
                      borderRadius: 2,
                      textAlign: "center"
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => finishEditingFloorName()}
                    style={{
                      padding: "2px 6px",
                      fontSize: "10px",
                      cursor: "pointer",
                      border: "1px solid #22c55e",
                      backgroundColor: "#22c55e",
                      color: "white",
                      borderRadius: 2
                    }}
                    title="保存"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => cancelEditingFloorName(floorKey)}
                    style={{
                      padding: "2px 6px",
                      fontSize: "10px",
                      cursor: "pointer",
                      border: "1px solid #ef4444",
                      backgroundColor: "#ef4444",
                      color: "white",
                      borderRadius: 2
                    }}
                    title="キャンセル"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => handleChangeFloor(floorKey)}
                    disabled={currentFloor === floorKey}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      cursor: "pointer",
                      border: currentFloor === floorKey ? "2px solid #3182ce" : "1px solid #ccc",
                      backgroundColor: currentFloor === floorKey ? "#ebf8ff" : "white",
                      borderRadius: 2,
                      minWidth: 60
                    }}
                  >
                    {floorNames[floorKey]}
                  </button>
                  <button
                    onClick={() => startEditingFloorName(floorKey)}
                    style={{
                      padding: "2px 6px",
                      fontSize: "10px",
                      cursor: "pointer",
                      border: "1px solid #6b7280",
                      backgroundColor: "#6b7280",
                      color: "white",
                      borderRadius: 2,
                      minWidth: 20
                    }}
                    title="フロア名を編集"
                  >
                    ✏️
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* 保存して閉じるボタン */}
        <div style={{ display: "flex", gap: 8 }}>
          {isAdmin && (
            <button
              onClick={handleExportDXF}
              style={{
                backgroundColor: "#eab308",
                color: "#FFFFFF",
                padding: "8px 16px",
                fontSize: "16px",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
              title="CADのDXF形式でエクスポート"
            >
              DXF出力
            </button>
          )}
          <button
            onClick={handleTempSave}
            disabled={isSaving}
            style={{
              backgroundColor: isSaving ? "#cccccc" : "#666666",
              color: "#FFFFFF",
              padding: "8px 16px",
              fontSize: "16px",
              border: "none",
              borderRadius: 4,
              cursor: isSaving ? "not-allowed" : "pointer",
            }}
          >
            {isSaving ? "保存中..." : "一時保存"}
          </button>
          <button
            onClick={handleSaveAndClose}
            disabled={isSaving}
            style={{
              backgroundColor: isSaving ? "#cccccc" : "#000000",
              color: "#FFFFFF",
              padding: "8px 16px",
              fontSize: "16px",
              border: "none",
              borderRadius: 4,
              cursor: isSaving ? "not-allowed" : "pointer",
            }}
          >
            {isSaving ? "保存中..." : "保存して閉じる"}
          </button>
        </div>
      </div>
    </div>
  );
}
